import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import HistoryIcon from '@mui/icons-material/History';
import ConsoleAccessHistory from '@bumble/app/components/ConsoleAccessHistory';
import { ConsoleHistoryConnection } from '@bumble/app/utils/console-access-history';
import {
  GridColDef,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import NoData from '@bumble/app/components/NoData';
import { NetworkDeviceOnDiagPortWithSessions } from '@bumble/app/__generated__/data-contracts';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { PageLoading } from '@bumble/app/components/PageLoading';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import { NDConnectReady, NDConnectSSHReady } from '@bumble/app/components/network-devices';
import { formatNetworkInterface } from '@bumble/app/components/NetworkInterfaceSelect';
import { COLORS } from '@bumble/app/theme';

interface NDSessionsProps {
  data: NetworkDeviceOnDiagPortWithSessions | undefined;
  onSessionUpdate?: () => void;
  isRefetching?: boolean;
}

const NDSessionsContent = ({
  data,
  onSessionUpdate,
  isRefetching = false,
}: NDSessionsProps & {
  data: NetworkDeviceOnDiagPortWithSessions;
}) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const [historySelection, setHistorySelection] = useState<{
    connection: ConsoleHistoryConnection;
    label: string;
  } | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [isRefreshingAfterToggle, setIsRefreshingAfterToggle] = useState(false);
  const [togglingContent, setTogglingContent] = useState<string>('');
  const [showReadyDialog, setShowReadyDialog] = useState<boolean>(false);
  const [readyUrl, setReadyUrl] = useState<string>('');
  const [showSSHReadyDialog, setShowSSHReadyDialog] = useState<boolean>(false);
  const [sshData, setSSHData] = useState<any>(null);

  const connectSession = useMutation(api.connectNetworkRemoteAccessApiV1ConnectNetworkRemoteAccessPost, {
    onSuccess(res: any) {
      toast.success('A new connection added successfully.');
      setReadyUrl(res.data?.web_url ?? '');
      setLoadingModalOpen(false);
      setShowReadyDialog(true);
    },
    onError(error: any) {
      setLoadingModalOpen(false);
      apiError(error);
    },
  });

  const connectSSHSession = useMutation(api.connectRawSshAccessApiV1ConnectRawSshAccessPost, {
    onSuccess(res: any) {
      toast.success('A new connection added successfully.');
      setSSHData(res.data);
      setLoadingModalOpen(false);
      setShowSSHReadyDialog(true);
    },
    onError(error: any) {
      setLoadingModalOpen(false);
      apiError(error);
    },
  });

  const disconnectSession = useMutation(
    api.disconnectNetworkRemoteAccessApiV1DisconnectNetworkRemoteAccessPost,
    {
      onSuccess() {
        toast.success('Session disconnected successfully.');
        setShowConfirmDialog(false);
        setLoadingModalOpen(false);
        handleRefresh();
        setRowSelectionModel([]);
      },
      onError(error: any) {
        apiError(error);
        setShowConfirmDialog(false);
        setLoadingModalOpen(false);
      },
    }
  );

  const disconnectSSHSession = useMutation(
    api.disconnectRawSshAccessApiV1DisconnectRawSshAccessPost,
    {
      onSuccess() {
        toast.success('Session disconnected successfully.');
        setShowConfirmDialog(false);
        setLoadingModalOpen(false);
        handleRefresh();
        setRowSelectionModel([]);
      },
      onError(error: any) {
        apiError(error);
        setShowConfirmDialog(false);
        setLoadingModalOpen(false);
      },
    }
  );

  const closeConsole = () => {
    setShowReadyDialog(false);
  };

  const closeSSHConsole = () => {
    setShowSSHReadyDialog(false);
  };


  const sessions = useMemo(
    () => data.sessions?.map((s: any) => ({ ...s, id: s.hw_id_path })) || [],
    [data.sessions],
  );

  const selectedSession = sessions.find((session: any) =>
    session.id === rowSelectionModel[0]
  );

  const handleDisconnect = () => {
    if (!selectedSession) return;
    setLoadingModalOpen(true);
    if (selectedSession.eth_console_protocol === 'ssh') {
      disconnectSSHSession.mutate({
        cpe_id: data.node_id,
        target_ip: selectedSession.eth_target_ip,
        target_port: selectedSession.eth_console_tcp_port,
      });
    } else {
      disconnectSession.mutate({
        cpe_id: data.node_id,
        target_ip: selectedSession.eth_target_ip,
        target_port: selectedSession.eth_console_tcp_port,
        protocol: selectedSession.eth_console_protocol
      });
    }
  };

  const handleDirectConnect = () => {
    if (!selectedSession) return;
    setLoadingModalOpen(true);
    // Reuse whichever interface this session was originally connected over —
    // reconnecting must not silently switch it to another interface.
    if (selectedSession.eth_console_protocol === 'ssh') {
      connectSSHSession.mutate({
        cpe_id: data.node_id,
        mac_addr: data.mac_addr,
        target_ip: selectedSession.eth_target_ip,
        target_port: selectedSession.eth_console_tcp_port,
        interface: selectedSession.interface,
      });
    } else {
      connectSession.mutate({
        cpe_id: data.node_id,
        mac_addr: data.mac_addr,
        target_ip: selectedSession.eth_target_ip,
        target_port: selectedSession.eth_console_tcp_port,
        protocol: selectedSession.eth_console_protocol,
        interface: selectedSession.interface,
      });
    }
  };

  const handleHistory = () => {
    if (
      !selectedSession?.eth_target_ip ||
      !selectedSession.eth_console_tcp_port ||
      !selectedSession.eth_console_protocol
    ) return;
    const connection: ConsoleHistoryConnection = {
      cpe_id: data.node_id,
      access_type: 'eth',
      target_ip: selectedSession.eth_target_ip,
      target_port: selectedSession.eth_console_tcp_port,
      protocol: selectedSession.eth_console_protocol,
    };
    setHistorySelection({
      connection,
      label: `${connection.target_ip}:${connection.target_port} (${connection.protocol.toUpperCase()})`,
    });
    queryClient.invalidateQueries(['console-access-history', connection]);
  };

  const menuItems = [
    {
      label: 'Connect',
      icon: <LinkIcon color="info" fontSize="small" />,
      onClick: handleDirectConnect,
      hasDivider: true,
      disabled: rowSelectionModel.length !== 1
    },
    {
      label: 'Disconnect',
      icon: <LinkOffIcon color="error" fontSize="small" />,
      onClick: () => { setShowConfirmDialog(true); },
      hasDivider: true,
      disabled: rowSelectionModel.length !== 1
    },
    {
      label: 'History',
      icon: <HistoryIcon color="info" fontSize="small" />,
      onClick: handleHistory,
      disabled:
        !selectedSession?.eth_target_ip ||
        !selectedSession.eth_console_tcp_port ||
        !selectedSession.eth_console_protocol,
    },
  ];

  const columns: GridColDef[] = [
    {
      field: 'eth_target_ip',
      headerName: 'Target IP',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'eth_console_tcp_port',
      headerName: 'Target Port',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'eth_console_protocol',
      headerName: 'Protocol',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'interface',
      headerName: 'Network Interface',
      width: 180,
      disableColumnMenu: true,
      valueGetter: (value) => formatNetworkInterface(value),
    },
  ];

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
    if (newSelection.slice(-1)[0] !== rowSelectionModel[0]) setHistorySelection(null);
  };

  const handleRefresh = () => {
    if (onSessionUpdate) {
      onSessionUpdate();
      toast.success('Refreshed session data.');
    }
  };

  const prevIsRefetchingRef = useRef(isRefetching);
  useEffect(() => {
    if (prevIsRefetchingRef.current && !isRefetching && isRefreshingAfterToggle) {
      setIsRefreshingAfterToggle(false);
      setTogglingContent('');
    }
    prevIsRefetchingRef.current = isRefetching;
  }, [isRefetching, isRefreshingAfterToggle]);

  const showLoader = isRefreshingAfterToggle;

  return (
    <>
      {showLoader && <PageLoading content={togglingContent} />}
      {loadingModalOpen && <PageLoading content="Working on it..." />}
      <Typography variant="h3" component="h1" sx={{ color: COLORS.TEXT, pr: 2, mb: 3 }}>
        Connections
      </Typography>
      <DataTableGrid
        rows={sessions}
        columns={columns}
        pagination
        rowSelectionModel={rowSelectionModel}
        handleSelectionChange={handleSelectionChange}
        itemsSelected={true}
        refreshData={handleRefresh}
        isRefetching={showLoader ? false : isRefetching}
        menuItems={menuItems}
      />
      {historySelection && (
        <ConsoleAccessHistory
          connection={historySelection.connection}
          label={historySelection.label}
          onClose={() => setHistorySelection(null)}
        />
      )}
      {showConfirmDialog && (
        <DialogConfirm
          open={showConfirmDialog}
          handleClose={() => setShowConfirmDialog(false)}
          handleConfirm={handleDisconnect}
          title="Disconnect session?"
          description={`Are you certain you wish to proceed with disconnecting ${rowSelectionModel[0]}? Please note that this action is irreversible.`}
        />
      )}
      {showReadyDialog && (
        <NDConnectReady
          handleClose={closeConsole}
          readyUrl={readyUrl}
          showReadyDialog={showReadyDialog}
        />
      )}
      {showSSHReadyDialog && (
        <NDConnectSSHReady
          handleClose={closeSSHConsole}
          data={sshData}
          showReadyDialog={showSSHReadyDialog}
        />
      )}
    </>
  );
};

export const NDSessions = ({ data, ...props }: NDSessionsProps) => (
  data ? (
    <NDSessionsContent key={`${data.node_id}:${data.mac_addr}`} data={data} {...props} />
  ) : <NoData />
);
